/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  makeStyles,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Link,
  Tooltip,
} from '@material-ui/core';
import moment from 'moment';
import { useLanguage } from 'hooks';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import LaunchIcon from '@material-ui/icons/Launch';
import TocRoundedIcon from '@material-ui/icons/TocRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import NavigateNextRoundedIcon from '@material-ui/icons/NavigateNextRounded';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { SERVICE_OFFERS_PUBLIC_PAGE, COLOR, ENG, isDev } from 'config/constants';
import { Picture } from 'models';
import { useIntl } from 'react-intl';
import { images } from 'assets/images';

interface Props {
  listing: any;
  setImageCarouselOpen?: (open: boolean) => void;
  setImageCarouselPictures?: (pictures: Picture[]) => void;
  draggable?: boolean;
  removeListing?: () => void;
  className?: string;
  classes?: { [key: string]: string };
  found_centris_listing?: any;
  style?: any;
  is_white_text?: boolean;
}

const useStyles = makeStyles({
  card: {
    width: 320,
    height: 500,
    marginBottom: 20,
  },
  cardThumbnail: {
    height: 300,
  },
  listingAddress: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  editCardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 35,
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 54,
  },
  serviceOfferIcon: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
    boxShadow: '0px 3px 5px 0px rgba(0,0,0,0.15)',
    width: 30,
    height: 30,
    padding: 5,
    transition: 'boxShadow 1s ease-in-out',
    marginBottom: 5,
  },
  serviceOfferIconContainer: {
    display: 'flex',
    marginTop: 10,
    marginBottom: 10,
  },
  serviceOffer: {
    display: 'flex',
    flexDirection: 'column',
    width: 50,
    marginRight: 10,
  },
  cardContent: {
    height: 100,
  },
  virtualTourCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 5,
    padding: 5,
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 10,
    right: 10,
    zIndex: 3,
    cursor: 'pointer',
    maxWidth: 150,
  },
  detailMediaLink: {
    display: 'flex',
    alignItems: 'center',
  },
  mediaLinkTitle: {
    fontWeight: 'bold',
    fontSize: 12,
    marginRight: 5,
  },
  mediaDeliveryLink: {
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    height: 54,
    '& .MuiTypography-root': {
      fontFamily: 'FordAntenna-SemiBold',
      marginRight: 5,
      textTransform: 'uppercase',
    },
  },
  dragIcon: {
    position: 'absolute',
    top: 5,
    left: 5,
    backgroundColor: COLOR.WHITE,
    borderRadius: 3,
  },
  deleteIcon: {
    fontSize: 30,
    color: COLOR.RED,
    marginRight: 10,
    alignItems: 'center',
  },
  roomCountContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    alignSelf: 'center',
  },
  roomCountText: {
    fontSize: 16,
    alignSelf: 'center',
  },
  detailTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: COLOR.DARK_GREY,
  },
  icon: {
    fontSize: 15,
    height: 15,
    marginRight: 10,
    width: 15,
    fill: 'grey',
    alignSelf: 'center',
  },
  twoColumnsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  municipalityText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: COLOR.DARK_GREY,
  },
  priceText: {
    color: COLOR.WHITE,
    fontSize: 16,
  },
  newListingContainer: {
    position: 'absolute',
    width: 175,
    top: 25,
    left: -50,
    textAlign: 'center',
    transform: 'rotate(-45deg)',
    textTransform: 'uppercase',
    zIndex: 2,
    background: COLOR.GREEN,
  },
  soldContainer: {
    position: 'absolute',
    width: 175,
    top: 25,
    left: -50,
    textAlign: 'center',
    transform: 'rotate(-45deg)',
    textTransform: 'uppercase',
    zIndex: 2,
    background: COLOR.RED,
  },
  codeStatusText: {
    color: COLOR.WHITE,
    fontWeight: 'bold',
    fontSize: 15,
  },
  whiteIcon: {
    color: COLOR.WHITE,
  },
  darkIcon: {
    color: COLOR.BLACK,
  },
  leftArrow: {
    position: 'absolute',
    top: '50%',
    left: '10px',
    transform: 'translateY(-50%)',
    color: 'white',
    cursor: 'pointer',
  },
  rightArrow: {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    color: 'white',
    cursor: 'pointer',
  },
});

function listingShowcaseItem(props: Props) {
  const {
    listing,
    setImageCarouselOpen,
    setImageCarouselPictures,
    draggable,
    removeListing,
    className,
    classes,
    found_centris_listing,
    style,
    is_white_text,
  } = props;
  const styles = useStyles();
  const language = useLanguage();
  const taxSuffix = language === ENG ? 'GST/QST' : 'TPS/TVQ';
  const or = language === ENG ? 'or' : 'ou';
  const location = useLocation();
  const dev_domain_name = new URLSearchParams(location.search).get('domain_name');
  const { cardThumbnailClass, buttonTextClass, iconClass } = classes;
  const admin_usage = window.location.hostname === 'hausportal.hausvalet.ca';
  const intl = useIntl();
  const fmtMediaPage = intl.formatMessage({ id: 'portal.mediaPage' });
  const fmtVirtualTour = intl.formatMessage({ id: 'portal.virtualTour' });
  const fmtDetails = intl.formatMessage({ id: 'portal.detailSheet' });
  const fmtReorderListings = intl.formatMessage({ id: 'portal.reoderListings' });
  const fmtNew = intl.formatMessage({ id: 'portal.new' });
  const fmtSold = intl.formatMessage({ id: 'portal.sold' });

  const secondary_color = style?.secondary_color || COLOR.SECONDARY_BLUE;
  const custom_icon_color = is_white_text ? styles.whiteIcon : styles.darkIcon;
  const iconStyle = style?.primary_color ? custom_icon_color : styles.whiteIcon;

  const distribution_token =
    listing.distribution_token || found_centris_listing?.distribution_token;
  const listing_date = moment(listing.listing_date);
  const service_offers = listing.service_offers || found_centris_listing?.service_offers;
  let address = '';
  if (found_centris_listing) {
    address = found_centris_listing.address;
  } else if (listing.addressAndCityOnly) {
    address = listing.addressAndCityOnly;
  } else {
    address = `${listing?.civic_number_start} ${listing.street}`;
  }

  const service_offer_icons = {
    drone: images.drone_dark,
    blue_hour: images.blue_hour_dark,
    virtual_staging: images.virtualStagingDark,
    floorplan: images.floorplan_dark,
    video: images.video_dark,
    virtual_tour: images.video360_dark,
    shooting_package: images.camera_dark,
    matterport: images.matterport_svg,
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  function loadServiceOffers() {
    return service_offers.map((service_offer) => {
      let service_offer_icon = null;
      let service_offer_name = null;

      for (const key in SERVICE_OFFERS_PUBLIC_PAGE) {
        if (SERVICE_OFFERS_PUBLIC_PAGE[key].includes(service_offer.id)) {
          service_offer_icon = service_offer_icons[key];
          service_offer_name = intl.formatMessage({ id: `portal.serviceOffer_${key}` });
          break;
        }
      }

      if (service_offer_icon === null) {
        return null;
      }
      return (
        <Box key={service_offer.id} className={styles.serviceOffer}>
          <Tooltip
            title={
              <>
                <Typography variant='subtitle1'>{service_offer_name}</Typography>
              </>
            }
          >
            <Box className={classNames(iconClass, styles.serviceOfferIcon)}>
              <img
                className={classes.icon}
                width={30}
                height={30}
                src={service_offer_icon}
                alt='service offer icon'
              />
            </Box>
          </Tooltip>
        </Box>
      );
    });
  }
  const get_display_price = () => {
    let display_price = '';
    const options = {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    };
    if (listing.price && listing.price > 1) {
      display_price = `${listing.price.toLocaleString('en-CA', options)}`;
    }
    if (listing.price === 1 && listing.location_price && listing.rent_price_frequency) {
      display_price = `${listing.location_price.toLocaleString('en-CA', options)} / ${
        listing.rent_price_frequency
      }`;
    }
    if (listing.price > 1 && listing.location_price > 1 && listing.rent_price_frequency) {
      display_price = `${listing.price.toLocaleString(
        'en-CA',
        options,
      )} ${or} ${listing.location_price.toLocaleString('en-CA', options)} / ${
        listing.rent_price_frequency
      }`;
    }
    if (listing.price_plus_taxes) {
      display_price = `${listing.price_plus_taxes.toLocaleString('en-CA', options)} + ${taxSuffix}`;
    }
    if (listing.price_including_taxes) {
      display_price = `${listing.price_including_taxes.toLocaleString('en-CA', options)}`;
    }
    return display_price;
  };

  const changeImage = (newIndex) => {
    setFade(false); // Trigger fade-out
    setTimeout(() => {
      setCurrentImageIndex(newIndex);
      setFade(true); // Trigger fade-in
    }, 300); // Duration should match CSS transition time
  };

  const goToPreviousImage = () => {
    const newIndex = currentImageIndex === 0 ? listing.pictures.length - 1 : currentImageIndex - 1;
    changeImage(newIndex);
  };

  const goToNextImage = () => {
    const newIndex = currentImageIndex === listing.pictures.length - 1 ? 0 : currentImageIndex + 1;
    changeImage(newIndex);
  };

  return (
    <Card className={classNames(className, styles.card)} variant='outlined'>
      <Box style={{ position: 'relative' }}>
        <Box className={styles.dragIcon}>
          {draggable ? (
            <Tooltip title={fmtReorderListings}>
              <TocRoundedIcon />
            </Tooltip>
          ) : null}
        </Box>
        {listing.url_params && (
          <Box
            onClick={() => {
              window.open(listing.virtualVisitURL, '_blank', 'popup');
            }}
            className={styles.virtualTourCard}
          >
            <Typography style={{ marginRight: 5 }}>{fmtVirtualTour}</Typography>
            <img width={25} height={25} src={images.video360_white} alt='virtual tour icon' />
          </Box>
        )}
        {listing.virtual_visit_url_fr && (
          <Box
            onClick={() => {
              window.open(listing.virtual_visit_url_fr, '_blank', 'popup');
            }}
            className={styles.virtualTourCard}
          >
            <Typography style={{ marginRight: 5 }}>{fmtVirtualTour}</Typography>
            <img width={25} height={25} src={images.video360_white} alt='virtual tour icon' />
          </Box>
        )}
        {listing.listing_date && Math.abs(listing_date.diff(moment(), 'days')) < 10 && (
          <Box className={styles.newListingContainer}>
            <Typography className={styles.codeStatusText}>{fmtNew}</Typography>
          </Box>
        )}
        {listing.code_status === 'VE' && listing.date_sold && (
          <Box className={styles.soldContainer}>
            <Typography className={styles.codeStatusText}>{fmtSold}</Typography>
          </Box>
        )}
        <CardMedia
          onClick={() => {
            setImageCarouselOpen(true);
            setImageCarouselPictures(listing.pictures);
          }}
          style={{
            cursor: 'pointer',
            transition: 'opacity 0.1s ease-in-out',
            opacity: fade ? 1 : 0,
          }}
          className={classNames(cardThumbnailClass, styles.cardThumbnail)}
          image={
            listing.thumbnail ||
            listing.pictures[currentImageIndex]?.thumbnail_url ||
            images.no_thumbnail
          }
          title='House picture'
        />
        {listing.pictures.length > 1 && (
          <IconButton onClick={goToPreviousImage} className={styles.leftArrow}>
            <ChevronLeftRoundedIcon />
          </IconButton>
        )}
        {listing.pictures.length > 1 && (
          <IconButton onClick={goToNextImage} className={styles.rightArrow}>
            <NavigateNextRoundedIcon />
          </IconButton>
        )}
      </Box>
      <CardContent className={styles.cardContent}>
        {service_offers && (
          <Box
            className={styles.serviceOfferIconContainer}
            style={service_offers.length <= 3 ? { justifyContent: 'flex-start' } : null}
          >
            {loadServiceOffers()}
          </Box>
        )}
        <Box className={styles.twoColumnsRow}>
          <Box>
            <Typography className={styles.listingAddress}>{address}</Typography>
            {listing.municipality && listing.municipality.description && (
              <Typography className={styles.municipalityText}>
                {listing.municipality.description}
              </Typography>
            )}
            {listing.mls && (
              <Typography className={styles.detailTitle}>{`MLS# ${listing.mls}`}</Typography>
            )}
            {listing.url_description && distribution_token && (
              <Link
                component='button'
                className={styles.detailMediaLink}
                onClick={() => {
                  if (admin_usage) {
                    window.open(
                      `https://media.hausvalet.ca/delivery/${distribution_token}`,
                      '_blank',
                    );
                  } else {
                    window.open(
                      `https://media.hausvalet.ca/public-media-page/${distribution_token}`,
                      '_blank',
                    );
                  }
                }}
              >
                <Typography
                  className={styles.mediaLinkTitle}
                  style={{ color: style?.primary_color || COLOR.LIGHT_BLUE }}
                >
                  {fmtMediaPage}
                </Typography>
                <LaunchIcon style={{ color: style?.primary_color || COLOR.LIGHT_BLUE }} />
              </Link>
            )}
          </Box>
          <Box>
            {listing.bedroom_count && listing.bedroom_count > 0 && (
              <Box className={styles.roomCountContent}>
                <img className={styles.icon} src={images.bedroom_icon} alt='bedroom_icon' />
                <Typography className={styles.roomCountText}>{listing.bedroom_count}</Typography>
              </Box>
            )}
            {listing.bathroom_count && listing.bathroom_count > 0 && (
              <Box className={styles.roomCountContent}>
                <img className={styles.icon} src={images.bathtub_icon} alt='bathtub_icon' />
                <Typography className={styles.roomCountText}>{listing.bathroom_count}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
      <CardActions
        className={draggable ? styles.editCardActions : styles.cardActions}
        style={{
          justifyContent: draggable || get_display_price() ? 'space-between' : 'flex-end',
          backgroundColor: secondary_color,
        }}
      >
        {draggable ? (
          <IconButton
            onClick={() => {
              return removeListing();
            }}
          >
            <DeleteOutlineIcon className={styles.deleteIcon} />
          </IconButton>
        ) : null}
        {get_display_price() && (
          <Typography className={styles.priceText}>
            {get_display_price()}
            {listing.currency && listing.currency.symbol}
          </Typography>
        )}
        {listing.url_description && (
          <Link
            component='button'
            className={styles.mediaDeliveryLink}
            onClick={() => {
              if (isDev()) {
                return window.open(
                  `/listing/${listing.mls}?domain_name=${dev_domain_name}`,
                  '__blank',
                );
              }
              return window.open(`/listing/${listing.mls}`, '__blank');
            }}
          >
            <Typography className={classNames(buttonTextClass)}>{fmtDetails}</Typography>
            <LaunchIcon className={iconStyle} />
          </Link>
        )}
        {!listing.url_description && distribution_token && (
          <Link
            component='button'
            className={styles.mediaDeliveryLink}
            onClick={() => {
              if (admin_usage) {
                window.open(`https://media.hausvalet.ca/delivery/${distribution_token}`, '_blank');
              } else {
                window.open(
                  `https://media.hausvalet.ca/public-media-page/${distribution_token}`,
                  '_blank',
                );
              }
            }}
          >
            <Typography className={classNames(buttonTextClass)}>{fmtMediaPage}</Typography>
            <LaunchIcon className={iconStyle} />
          </Link>
        )}
      </CardActions>
    </Card>
  );
}

export default listingShowcaseItem;
